import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
interface Message {
  role: 'user' | 'assistant';
  content: string;
}
interface AIChatbotProps {
  onJobDescriptionChange?: (description: string) => void;
  selectedCriteria?: {
    country: string;
    experience: string;
    site: string;
    profession: string;
    discipline: string;
    role_type: string;
    employment_type: string;
  };
  className?: string;
}
const formatMessage = (content: string) => {
  const parts = content.split('**');
  return parts.map((part, index) => {
    if (index % 2 === 1) {
      const isEndOfLine = part.endsWith('\n') || index === parts.length - 1;
      return isEndOfLine ? `\n${part}\n\n` : `\n${part}\n`;
    }
    return part;
  }).join('');
};
export function AIChatbot({
  onJobDescriptionChange,
  selectedCriteria,
  className
}: AIChatbotProps) {
  const [messages, setMessages] = useState<Message[]>([{
    role: 'assistant',
    content: 'Hello! I can help you create job descriptions. Please provide the role requirements, and I\'ll generate a detailed job description taking into account your selected criteria.'
  }]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [aiModel, setAiModel] = useState('openai');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  const formatCriteria = () => {
    if (!selectedCriteria) return '';
    const criteria = [];
    if (selectedCriteria.country) criteria.push(`Location: ${selectedCriteria.country}`);
    if (selectedCriteria.experience) criteria.push(`Experience Level: ${selectedCriteria.experience}`);
    if (selectedCriteria.site) criteria.push(`Work Site: ${selectedCriteria.site}`);
    if (selectedCriteria.profession) criteria.push(`Profession: ${selectedCriteria.profession}`);
    if (selectedCriteria.discipline) criteria.push(`Discipline: ${selectedCriteria.discipline}`);
    if (selectedCriteria.role_type) criteria.push(`Role Type: ${selectedCriteria.role_type}`);
    if (selectedCriteria.employment_type) criteria.push(`Employment Type: ${selectedCriteria.employment_type}`);
    return criteria.length > 0 ? `\n\nSelected Criteria:\n${criteria.join('\n')}` : '';
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage
    }]);
    setIsLoading(true);
    try {
      const modelToUse = aiModel.toLowerCase();
      const {
        data,
        error
      } = await supabase.functions.invoke('generate-job-description', {
        body: {
          role: "Role from requirements",
          requirements: userMessage + formatCriteria(),
          aiModel: modelToUse
        }
      });
      if (error) throw error;
      const formattedDescription = formatMessage(data.jobDescription);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: formattedDescription
      }]);
      onJobDescriptionChange?.(formattedDescription);
    } catch (error) {
      console.error('Error generating job description:', error);
      toast.error("Failed to generate job description. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  return <div className={cn("bg-secondary/50 backdrop-blur-lg border border-primary/20 rounded-lg shadow-lg shadow-primary/5 p-4 h-[450px] flex flex-col", className, {
    "border-2 border-primary": className?.includes("border-2 border-white")
  })}>
      <div className="flex flex-col gap-2 mb-3">
        <h3 className="text-lg font-semibold text-white">Step 3: Use AI JD Generator</h3>
        <div className="flex justify-center items-center gap-4">
          <Label className="text-[12px] text-white">Choose AI Engine:</Label>
          <RadioGroup defaultValue="openai" value={aiModel} onValueChange={setAiModel} className="flex items-center gap-4">
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="openai" id="ai-openai" />
              <Label htmlFor="ai-openai" className="text-[12px] text-primary-light">OpenAI</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="deepseek" id="ai-deepseek" />
              <Label htmlFor="ai-deepseek" className="text-[12px] text-primary-light">DeepSeek</Label>
            </div>
          </RadioGroup>
        </div>
      </div>
      <ScrollArea className="flex-1 pr-4 mb-4">
        <div className="space-y-3">
          {messages.map((message, index) => <div key={index} className={`p-2 rounded-lg text-xs whitespace-pre-wrap text-left ${message.role === 'assistant' ? 'bg-primary/10 text-primary-light' : 'bg-white/10 text-primary-light ml-4'}`}>
              {message.content}
            </div>)}
          {isLoading && <div className="flex items-center justify-center py-3">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      <form onSubmit={handleSubmit} className="flex gap-2 mt-auto">
        <Textarea value={input} onChange={e => setInput(e.target.value)} placeholder="Describe the role requirements..." className="flex-1 bg-black text-white border-primary/20 text-sm h-[80px] placeholder:text-gray-400" />
        <Button type="submit" disabled={isLoading || !input.trim()} className="bg-primary hover:bg-primary-dark text-white text-sm">
          Send
        </Button>
      </form>
    </div>;
}